// ============================================================
// Grid Audio Controller — direct-URL streaming TTS.
// ============================================================
// This is intentionally the simplest possible playback path:
//   const el = new Audio(`/api/tts/speak?character=…&text=…`);
//   el.play();
// No blob URLs, no createObjectURL, no CORS gymnastics. The URL is
// same-origin (all `/api/*` requests are proxied to the FastAPI backend
// via Kubernetes ingress), so the browser handles range streaming and
// caching natively.
//
// Public API:
//   ensureAudioContext()            - unlock the AudioContext on user gesture
//   isGridInitialized()             - has the user tapped the gate?
//   markGridInitialized()           - mark gate as tapped
//   unlockAudio()                   - explicit unlock (call on any tap)
//   speakLine(character, text, opts)- primary TTS API
//   speakAs(character, keyOrText, opts) - back-compat shim
//   playClip(character, keyOrText, opts) - same as speakAs
//   replayGreeting(character, key)  - convenience for a route greeting
//   playEntryChime()                - synthesised chime (Web Audio)
//   getAudioStatus() / subscribeAudioStatus(fn)  - diagnostic hooks
//   CHARACTER_META / GREETINGS      - character + greeting registry
//
// Diagnostic status transitions:
//   "locked"  → user hasn't tapped yet
//   "ready"   → gate tapped, at least one clip has started successfully
//   "error"   → last playback attempt failed (network, HTTP, or blocked)

const GATE_KEY = "wa_grid_entered_v1";

const BACKEND =
  (typeof window !== "undefined" && window.__BACKEND_URL__) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

// ------------------------------------------------------------
// Diagnostic status (locked | ready | error)
// ------------------------------------------------------------

let audioStatus = "locked";
let lastError = null;
const statusListeners = new Set();

export function getAudioStatus() {
  return { status: audioStatus, error: lastError };
}

export function subscribeAudioStatus(fn) {
  statusListeners.add(fn);
  try { fn(getAudioStatus()); } catch (_) { /* noop */ }
  return () => statusListeners.delete(fn);
}

function setStatus(next, err = null) {
  audioStatus = next;
  lastError = err;
  statusListeners.forEach((fn) => {
    try { fn(getAudioStatus()); } catch (_) { /* noop */ }
  });
}

// ------------------------------------------------------------
// AudioContext lifecycle
// ------------------------------------------------------------

let sharedCtx = null;

export function ensureAudioContext() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!sharedCtx) sharedCtx = new AC();
    if (sharedCtx.state === "suspended" && sharedCtx.resume) {
      sharedCtx.resume().catch(() => {});
    }
    return sharedCtx;
  } catch (_) {
    return null;
  }
}

export function isGridInitialized() {
  try {
    return typeof window !== "undefined" && sessionStorage.getItem(GATE_KEY) === "1";
  } catch (_) {
    return false;
  }
}

export function markGridInitialized() {
  try { sessionStorage.setItem(GATE_KEY, "1"); } catch (_) { /* private mode */ }
}

/**
 * Explicit unlock — MUST be called from a real user gesture (button click,
 * touchend). Creates the AudioContext AND primes the HTMLAudio pipeline so
 * subsequent programmatic play() calls succeed under iOS Safari's autoplay
 * policy.
 */
export function unlockAudio() {
  ensureAudioContext();
  // Silent, muted primer — costs nothing and satisfies autoplay heuristics.
  try {
    const el = new Audio(
      "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA"
    );
    el.volume = 0;
    el.muted = true;
    const p = el.play();
    if (p && typeof p.then === "function") p.then(() => el.pause()).catch(() => {});
  } catch (_) { /* ignore */ }
  markGridInitialized();
  if (audioStatus === "locked") setStatus("ready");
}

// ------------------------------------------------------------
// Character + greeting registry
// ------------------------------------------------------------

export const CHARACTER_META = {
  alfred:  { name: "Alfred",  role: "Executive Concierge",     avatar: "/avatars/alfred.jpg", volume: 1.0 },
  amanda:  { name: "Amanda",  role: "Front Counter Presenter", avatar: "/avatars/amanda.jpg", volume: 1.0 },
  wingman: { name: "Wingman", role: "Ops Commander",           avatar: "/avatars/alfred.jpg", volume: 1.0 },
  bentley: { name: "Bentley", role: "Adventure Captain",       avatar: "/avatars/alfred.jpg", volume: 1.0 },
};

export const GREETINGS = {
  alfred: {
    welcome:
      "Welcome back, operator. Alfred here. Let's line up your next move on the Grid.",
    home:
      "Welcome to the Last Fifty Mile A-I Utility Company. I'm Alfred. Choose your path — your team is standing by.",
    local:
      "Welcome back, operator. Alfred here. Let's line up your local assessment and get you into the Grid.",
    demo:
      "This is Alfred. Give me thirty seconds and I'll show you what your business looks like inside the Grid.",
  },
  amanda: {
    welcome:
      "Welcome, operator. I'm Amanda, your Flight School trainer. I'll walk you through every phase — take your time.",
    mission:
      "Let's start with the mission. We are the Last Fifty Mile A-I Utility Company. Our job is to reach the small businesses no one else drives to, and hand them the same tools the Fortune Five Hundred pay millions for. You are a field operator. Let's begin.",
    philosophy:
      "Before you touch a tool, we need to lock in the four pillars of the Grid. Read each one carefully. When you're ready, sign the manifesto below to commit — that's how we know the pillars are yours.",
    arsenal:
      "This is your Arsenal — the Resource Library. Four hooks, each ready to send to a prospect. You don't lecture — you send the link that does the talking. Let me show you which asset fits which conversation.",
    streetstrike:
      "This is the Street Strike. Drop a zip code and you'll see every door on the block. Every dot is a ninety-seven dollar Anchor. Your job — get the Anchor. Once we're inside, the Grid does the climbing to Titan.",
    tools:
      "Now the primary weapon — the Lead Hunter. Type a real prospect below and press Hunt. Alfred will audit them live and hand you three revenue leaks and a pitch. Practice mode is locked on, so nothing goes out until your commander clears you.",
    activation:
      "You made it. When you approve a real pitch out there, it drops into the Grid Activation Bridge and queues into the GVO Auto-Net. From that moment on, the Grid works while you sleep. You're cleared for duty, operator. Welcome to the ground floor.",
    moto:
      "Welcome to Iron and Oil. I'm Amanda, on the counter. Tell me what's under the hood and I'll route you.",
  },
  wingman: {
    welcome:
      "Recruit — this is Wingman, your ops commander. Welcome to Flight School. Follow my lead.",
    mission:
      "Recruit — this is Wingman. You just joined the Last Fifty Mile A-I Utility Company. Your mission: reach the small businesses no one else will drive to, and hand them the same tools the Fortune Five Hundred pay millions for. You are a field operator. I am your ops commander. Let's move.",
    philosophy:
      "Before you touch a tool, memorise the four pillars. This is who we are and what we sell. Ignore them and you'll close nothing. Live them and you'll never have to sell hard again.",
    arsenal:
      "This is your Arsenal. Every asset in the Resource Library is a hook. You don't lecture prospects — you send them one link that does the talking. Live demo, e-book, or Magic Mirror. Pick the right one, drop it, follow up.",
    tools:
      "This is the Lead Hunter Command. Paste a prospect's site, press Hunt, Alfred audits them in twelve seconds. You get three revenue leaks and a pitch. Read it. Approve it. Speed is the weapon.",
    activation:
      "The moment you approve a pitch, it drops into the Grid Activation Bridge and queues into the GVO Auto-Net. From there the Grid works while you sleep. You are cleared for duty, operator.",
  },
  bentley: {
    welcome:
      "Hey there, adventurer. I'm Bentley — the Adventure Captain of River Bend. Ready to explore?",
  },
};

// ------------------------------------------------------------
// Playback — global singleton lock
// ------------------------------------------------------------
// Only ONE audio element can play across the entire app at a time.
// Any new speakLine / playClip / speakAs call cancels the current one
// FIRST, then starts the new one. This eliminates overlapping voices
// no matter how fast the user navigates or clicks.

let currentAudio = null;

export function stopAllAudio() {
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch (_) { /* noop */ }
    currentAudio = null;
  }
}

function ttsUrl(character, text) {
  const base = BACKEND ? BACKEND.replace(/\/$/, "") : "";
  return `${base}/api/tts/speak?character=${encodeURIComponent(character)}&text=${encodeURIComponent(text)}`;
}

async function playTTS(character, text, opts = {}) {
  const c = CHARACTER_META[character];
  if (!c || !text) return;

  ensureAudioContext();

  // GLOBAL LOCK — kill any voice currently playing before starting the
  // new one. `interrupt: false` is honoured only when the caller has
  // explicitly opted out (e.g. a queued follow-up on the same character).
  if (opts.interrupt === false && currentAudio) {
    // Something else is speaking — drop this call rather than overlap.
    return;
  }
  stopAllAudio();

  const url = ttsUrl(character, text);
  console.log(`[audio] Playing TTS: ${character} "${text.slice(0, 60)}${text.length > 60 ? "…" : ""}"`);

  const el = new Audio(url);
  el.preload = "auto";
  el.volume = c.volume;

  const done = () => {
    if (currentAudio === el) currentAudio = null;
    try { opts.onEnd && opts.onEnd(); } catch (_) { /* noop */ }
  };
  el.addEventListener("ended", done, { once: true });
  el.addEventListener("error", () => {
    const errMsg = el.error ? `code ${el.error.code}` : "unknown";
    console.warn(`[audio] Playback error for ${character}: ${errMsg}`);
    setStatus("error", `Playback error (${errMsg})`);
    done();
  }, { once: true });

  currentAudio = el;
  try { opts.onStart && opts.onStart(); } catch (_) { /* noop */ }

  try {
    const p = el.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        console.log(`[audio] Playback started successfully (${character})`);
        setStatus("ready");
      }).catch((e) => {
        console.warn(`[audio] play() blocked/failed for ${character}:`, e?.message || e);
        setStatus("error", e?.message || String(e));
        done();
      });
    }
  } catch (e) {
    setStatus("error", String(e));
    done();
  }
}

// ------------------------------------------------------------
// Public helpers
// ------------------------------------------------------------

export function speakLine(character, text, opts = {}) {
  return playTTS(character, text, opts);
}

export function playClip(character, clipOrText, opts = {}) {
  const table = GREETINGS[character] || {};
  const text =
    typeof clipOrText === "string" && table[clipOrText]
      ? table[clipOrText]
      : (typeof clipOrText === "string" && clipOrText.trim().length > 0
          ? clipOrText
          : table.welcome || "");
  if (!text) return;
  return playTTS(character, text, opts);
}

// Back-compat for the many pages that already call speakAs.
export function speakAs(character, textOrClip, opts = {}) {
  return playClip(character, textOrClip, opts);
}

export function replayGreeting(character, routeKey = "welcome") {
  unlockAudio();
  const table = GREETINGS[character] || {};
  const text = table[routeKey] || table.welcome || "";
  if (!text) return;
  return playTTS(character, text);
}

// ------------------------------------------------------------
// Entry chime (Web Audio — synthesis, not a voice)
// ------------------------------------------------------------
export function playEntryChime() {
  const ctx = ensureAudioContext();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    const low = ctx.createOscillator();
    const lowGain = ctx.createGain();
    low.type = "sine";
    low.frequency.setValueAtTime(80, now);
    low.frequency.exponentialRampToValueAtTime(180, now + 0.9);
    lowGain.gain.setValueAtTime(0, now);
    lowGain.gain.linearRampToValueAtTime(0.14, now + 0.1);
    lowGain.gain.linearRampToValueAtTime(0, now + 1.0);
    low.connect(lowGain).connect(ctx.destination);
    low.start(now); low.stop(now + 1.05);

    const hi = ctx.createOscillator();
    const hiGain = ctx.createGain();
    hi.type = "triangle";
    hi.frequency.setValueAtTime(880, now);
    hi.frequency.linearRampToValueAtTime(1320, now + 0.6);
    hiGain.gain.setValueAtTime(0, now);
    hiGain.gain.linearRampToValueAtTime(0.05, now + 0.15);
    hiGain.gain.linearRampToValueAtTime(0, now + 0.8);
    hi.connect(hiGain).connect(ctx.destination);
    hi.start(now); hi.stop(now + 0.85);
  } catch (_) { /* ignore */ }
}
